import type { MutableRefObject } from "react";
import type { RichTextHighlight } from "@library-elements/text";
import {
  getHighlightSpanInParents,
  getHighlightsInSelection,
} from "../../../utils/highlight";
import { HIGHLIGHT_RESET_DELAY_MS } from "../constants";
import type { FroalaEditorInstance } from "../types";

export const handleHighlight = (
  editor: FroalaEditorInstance,
  el: HTMLElement,
  data: RichTextHighlight | null,
  highlightResetTimeout: MutableRefObject<NodeJS.Timeout | null>,
  handleAllSelection: () => boolean,
  refresh: (clearSelection?: boolean) => void,
) => {
  const isAllSelected = handleAllSelection();
  const sel = window.getSelection();
  if (!sel) return;

  const endEl = editor.selection.endElement();
  const blocks = editor.selection.blocks() as HTMLElement[];
  const highlightsInSelection: HTMLElement[] = [];
  for (const b of Array.from(blocks)) {
    highlightsInSelection.push(...getHighlightsInSelection(b));
  }
  const inBetween = blocks.slice(1, blocks.length - 1);

  if (data == null) {
    const removeIfIsHighlight = (element: HTMLElement) => {
      const hiSpan = getHighlightSpanInParents(element);
      if (hiSpan) {
        editor.$(hiSpan).replaceWith(hiSpan.innerHTML);
      }
    };

    removeIfIsHighlight(el);
    removeIfIsHighlight(endEl);
    for (const element of Array.from(inBetween)) {
      const his = element.querySelectorAll("[data-highlight-active]");
      for (const h of Array.from(his)) {
        editor.$(h).replaceWith(h.innerHTML);
      }
    }
    editor.save.save();
    refresh(isAllSelected);
    return;
  }

  if (highlightsInSelection.length === 1) {
    const hiSpan = highlightsInSelection[0];
    if (hiSpan) {
      editor.$(hiSpan).attr({
        "data-highlight-active": "false",
        "data-highlight-animation": data.animation as string,
        "data-highlight-bg-color": data.bgColor,
        "data-highlight-text-color": data.textColor,
        "data-highlight-static": data.static?.toString() ?? "false",
        style: `--var-highlight-bg-color: ${data.bgColor};--var-highlight-text-color: ${data.textColor};`,
      });

      if (highlightResetTimeout.current != null) {
        clearTimeout(highlightResetTimeout.current);
        highlightResetTimeout.current = null;
      }

      highlightResetTimeout.current = setTimeout(() => {
        hiSpan.setAttribute("data-highlight-active", "true");
        highlightResetTimeout.current = null;
      }, HIGHLIGHT_RESET_DELAY_MS);

      editor.save.save();
      refresh();
      return;
    }
  }

  for (const n of Array.from(highlightsInSelection)) {
    editor.$(n).replaceWith(n.innerHTML);
  }

  const wrapElInTmpSpan = (child: Element | DocumentFragment) => {
    const spanTmp = document.createElement("span");
    spanTmp.className = "fr-tmp-highlight";
    spanTmp.appendChild(child);
    return spanTmp;
  };

  /**
   * Check if a range contains contentEditable="false" elements (icons, emojis, RKPIs).
   * extractContents() can corrupt or split these, so we need a safe alternative.
   * Uses live DOM query instead of cloneContents() to avoid cloning the entire fragment.
   */
  const ATOMIC_INLINE_SELECTOR =
    '[contenteditable="false"], i[data-is-remix-icon], i[data-is-font-awesome-icon], em-emoji';

  const rangeHasCEFalse = (r: Range): boolean => {
    const ancestor = r.commonAncestorContainer;
    if (ancestor.nodeType === Node.TEXT_NODE) return false;
    const ceElements = (ancestor as Element).querySelectorAll?.(
      ATOMIC_INLINE_SELECTOR,
    );
    if (!ceElements || ceElements.length === 0) return false;
    // Verify at least one atomic inline element is actually within the range
    for (const el of Array.from(ceElements)) {
      if (r.intersectsNode(el)) return true;
    }
    return false;
  };

  /**
   * Safely extract range contents, preserving contentEditable="false" elements.
   * If no CE=false elements exist, uses the fast extractContents() path.
   * Otherwise, clones CE=false nodes and extracts text nodes normally.
   */
  const safeExtractContents = (r: Range): DocumentFragment => {
    if (!rangeHasCEFalse(r)) {
      return r.extractContents();
    }
    // Node-by-node approach: iterate child nodes in the range,
    // clone CE=false nodes (to avoid splitting them), extract everything else.
    const fragment = document.createDocumentFragment();
    const ancestor = r.commonAncestorContainer;

    if (ancestor.nodeType === Node.TEXT_NODE) {
      // Range is within a single text node — safe to extract
      return r.extractContents();
    }

    // Collect nodes that overlap with the range
    const walker = document.createTreeWalker(
      ancestor,
      NodeFilter.SHOW_ALL,
      {
        acceptNode: (node) => {
          const nodeRange = document.createRange();
          nodeRange.selectNode(node);
          if (
            r.compareBoundaryPoints(Range.END_TO_START, nodeRange) < 0 &&
            r.compareBoundaryPoints(Range.START_TO_END, nodeRange) > 0
          ) {
            return NodeFilter.FILTER_ACCEPT;
          }
          return NodeFilter.FILTER_SKIP;
        },
      },
    );

    const nodesToProcess: Node[] = [];
    let current = walker.nextNode();
    while (current) {
      // Only collect top-level children of the ancestor that intersect the range
      if (current.parentNode === ancestor) {
        nodesToProcess.push(current);
      }
      current = walker.nextNode();
    }

    for (const node of nodesToProcess) {
      if (
        node instanceof HTMLElement &&
        node.matches(ATOMIC_INLINE_SELECTOR)
      ) {
        // Clone atomic inline elements to preserve them, then remove original
        fragment.appendChild(node.cloneNode(true));
        node.parentNode?.removeChild(node);
      } else {
        // For regular nodes, create a range for this node and extract
        const nodeRange = document.createRange();
        nodeRange.selectNode(node);

        // Clamp to original range boundaries
        if (r.compareBoundaryPoints(Range.START_TO_START, nodeRange) > 0) {
          nodeRange.setStart(r.startContainer, r.startOffset);
        }
        if (r.compareBoundaryPoints(Range.END_TO_END, nodeRange) < 0) {
          nodeRange.setEnd(r.endContainer, r.endOffset);
        }

        fragment.appendChild(nodeRange.extractContents());
      }
    }

    return fragment;
  };

  if (editor.selection.isCollapsed()) return;
  if (sel.rangeCount === 0) return;
  const range = sel.getRangeAt(0);

  if (blocks.length > 1) {
    // Start
    const startBlock = blocks[0];
    const startRange = new Range();
    startRange.setStart(range.startContainer, range.startOffset);
    startRange.setEnd(startBlock, startBlock.childNodes.length);
    const startPart = safeExtractContents(startRange);
    startRange.insertNode(wrapElInTmpSpan(startPart));

    for (const n of Array.from(inBetween)) {
      const spanTmp = document.createElement("span");
      spanTmp.className = "fr-tmp-highlight";
      while (n.firstChild) {
        spanTmp.appendChild(n.firstChild);
      }
      n.appendChild(spanTmp);
    }

    // End
    const endRange = new Range();
    endRange.setStart(range.endContainer, 0);
    endRange.setEnd(range.endContainer, range.endOffset);
    const endPart = safeExtractContents(endRange);
    endRange.insertNode(wrapElInTmpSpan(endPart));
  } else {
    const selectedContent = safeExtractContents(range);
    range.insertNode(wrapElInTmpSpan(selectedContent));
  }

  let hasSavedRange = false;
  if (data) {
    for (const block of Array.from(blocks)) {
      const tmps = block.querySelectorAll(".fr-tmp-highlight");
      for (const tmp of Array.from(tmps)) {
        if (tmp instanceof HTMLElement) {
          if (!hasSavedRange && sel.rangeCount > 0) {
            hasSavedRange = true;
            const range = new Range();
            range.selectNodeContents(tmp);
            sel.removeAllRanges();
            sel.addRange(range);
          }
          tmp.removeAttribute("class");
          tmp.setAttribute("data-highlight-active", "false");
          tmp.setAttribute(
            "data-highlight-animation",
            data.animation as string,
          );
          tmp.setAttribute(
            "data-highlight-static",
            data.static?.toString() ?? "false",
          );
          tmp.setAttribute("data-highlight-bg-color", data.bgColor);
          tmp.setAttribute("data-highlight-text-color", data.textColor);
          tmp.style.setProperty("--var-highlight-bg-color", data.bgColor);
          tmp.style.setProperty(
            "--var-highlight-text-color",
            data.textColor,
          );
        }
      }
    }
  }

  for (const block of Array.from(blocks)) {
    const pending = block.querySelectorAll('[data-highlight-active="false"]');
    for (const el of Array.from(pending)) {
      void (el as HTMLElement).offsetHeight;
      el.setAttribute("data-highlight-active", "true");
    }
  }

  editor.save.save();
  refresh();
};
